import type { Locale } from "src/lib/Intl";
import {
  BRAND_SLUG_LOCALE_TRANSLATION,
  GENDER_TRANSLATIONS,
} from "src/utils/constants";
import {
  allBrandsTranslations,
  brandTranslations,
} from "src/utils/routeTranslations";
import {
  buildPageMarker,
  MAX_PAGINATION_PAGE,
  PAGE_QUERY_PARAM,
  parsePaginatedUrlPage,
  readPageMarker,
  toPublicAsPath,
} from "./page-url";

/** Segments under a gender with no server-rendered listing to paginate. */
const NON_PLP_SEGMENTS = new Set([
  "categories",
  "designers",
  "search",
  ...allBrandsTranslations,
]);

const BRAND_ROOTS = new Set<string>([
  "brands",
  ...allBrandsTranslations,
  ...Object.values(brandTranslations),
]);

function toSegments(pathname: string): string[] {
  return pathname.replace(/\/+$/, "").split("/").filter(Boolean);
}

export function toPublicBrandPath(path: string, locale: Locale): string {
  const [pathname, query] = path.split("?");
  const segments = toSegments(pathname);
  const publicRoot = BRAND_SLUG_LOCALE_TRANSLATION[locale];

  if (!publicRoot || !BRAND_ROOTS.has(segments[0])) {
    return path;
  }

  segments[0] = publicRoot;
  const publicPath = `/${segments.join("/")}`;

  return query ? `${publicPath}?${query}` : publicPath;
}

function findMarkerIndex(segments: string[]): number | null {
  const [first, second] = segments;

  if (BRAND_ROOTS.has(first)) {
    return segments.length === 2 || segments.length === 3
      ? segments.length
      : null;
  }

  if (first === "products") {
    return segments.length === 2 ? 1 : null;
  }

  if (segments.length < 2 || !GENDER_TRANSLATIONS.includes(first)) {
    return null;
  }

  if (segments.length === 2) {
    return NON_PLP_SEGMENTS.has(second) ? null : 0;
  }

  return second === "collections" ? 0 : null;
}

export function isPaginatedPlpPath(pathname: string): boolean {
  return findMarkerIndex(toSegments(pathname)) !== null;
}

function getPaginatedPathname(pathname: string, page: number): string | null {
  const segments = toSegments(pathname);
  const index = findMarkerIndex(segments);

  if (index === null) {
    return null;
  }

  segments[index] = `${segments[index] ?? ""}${buildPageMarker(page)}`;

  return `/${segments.join("/")}`;
}

type PaginationAction =
  | { type: "redirect"; path: string }
  | { type: "rewrite"; path: string };

export function resolvePaginationAction(
  pathname: string,
  search: string,
  resolvedPathname = pathname
): PaginationAction | null {
  if (readPageMarker(pathname) !== null) {
    const requested = `${pathname}${search}`;
    const publicPath = toPublicAsPath(requested);

    return publicPath === requested
      ? null
      : { type: "redirect", path: publicPath };
  }

  const params = new URLSearchParams(search);
  const values = params.getAll(PAGE_QUERY_PARAM);

  if (values.length === 0) {
    return null;
  }

  const page = values.length === 1 ? parsePaginatedUrlPage(values[0]) : null;
  const paginated =
    page !== null && page <= MAX_PAGINATION_PAGE
      ? getPaginatedPathname(resolvedPathname, page)
      : null;

  if (paginated) {
    return { type: "rewrite", path: paginated };
  }

  if (!isPaginatedPlpPath(resolvedPathname)) {
    return null;
  }

  params.delete(PAGE_QUERY_PARAM);
  const query = params.toString();

  return { type: "redirect", path: query ? `${pathname}?${query}` : pathname };
}

type CanonicalPath = {
  pageUrlSlug: string;
  canonicalPath: string;
  paginatedPage: number | null;
};

export function resolveCanonicalPath(asPath: string): CanonicalPath {
  const [pathWithHash, queryString = ""] = asPath.split("?");
  const pageUrlSlug = pathWithHash.split("#")[0];
  const paginatedPage = isPaginatedPlpPath(pageUrlSlug)
    ? parsePaginatedUrlPage(
        new URLSearchParams(queryString).get(PAGE_QUERY_PARAM)
      )
    : null;

  return {
    pageUrlSlug,
    paginatedPage,
    canonicalPath: paginatedPage
      ? `${pageUrlSlug}?${PAGE_QUERY_PARAM}=${paginatedPage}`
      : pageUrlSlug,
  };
}

type PaginationLinks = {
  prevPath: string | null;
  nextPath: string | null;
};

// The rel="prev" / rel="next" attributes for the paginated pages.
export function resolvePaginationLinks(
  pageUrlSlug: string,
  page: number,
  totalPages = 0
): PaginationLinks {
  if (!isPaginatedPlpPath(pageUrlSlug)) {
    return { prevPath: null, nextPath: null };
  }

  const lastPage = Math.min(totalPages, MAX_PAGINATION_PAGE);
  const neighbour = (target: number) => {
    if (target < 1 || target > lastPage) {
      return null;
    }

    return target === 1
      ? pageUrlSlug
      : `${pageUrlSlug}?${PAGE_QUERY_PARAM}=${target}`;
  };

  return { prevPath: neighbour(page - 1), nextPath: neighbour(page + 1) };
}
